import { ApiEndpoints } from "@shared/api/api-endpoints";
import { mxApiClient } from "@shared/api/clients/apiClientFactory";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";

export const addResource = async (payload: any): Promise<any> => {
  const url = ApiEndpoints.createUrl("mx", "device-management");
  const response = await mxApiClient.post(url, payload);
  return response.data;
};

export const searchSubmeters = async ({
  remoteUtilityId,
  search,
  page,
  limit,
}: {
  remoteUtilityId: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "master-meter/search-submeters",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      if (search) {
        query.push("search", search);
      }
      if (page) {
        query.push("page", page.toString());
      }
      if (limit) {
        query.push("limit", limit.toString());
      }
    },
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

export const getResources = async ({
  remoteUtilityId,
  searchData,
  physicalStatusTab,
  page,
  limit,
  utilityService,
  resourceType,
  resourceSubType,
  status,
}: {
  remoteUtilityId: string;
  searchData?: string;
  physicalStatusTab?: string;
  page?: number;
  limit?: number;
  utilityService?: string;
  resourceType?: string;
  resourceSubType?: string;
  status?: string;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "device-management/v1",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      if (searchData) {
        query.push("search_data", searchData);
      }
      if (physicalStatusTab) {
        query.push("physical_status_tab", physicalStatusTab);
      }
      if (page) {
        query.push("page", page.toString());
      }
      if (limit) {
        query.push("limit", limit.toString());
      }
      if (utilityService) {
        query.push("utility_service", utilityService);
      }
      if (resourceType) {
        query.push("resource_type", resourceType);
      }
      if (resourceSubType) {
        query.push("resource_sub_type", resourceSubType);
      }
      if (status) {
        query.push("status", status);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

export const getMeterSummary = async (remoteUtilityId: string) => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "mx",
    "meter",
    ["summary"],
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const getMeterDetail = async ({
  meterId,
  remoteUtilityId,
}: {
  meterId: string;
  remoteUtilityId?: string;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-detail",
    (query) => {
      query.push("meter_id", meterId.toString());
      if (remoteUtilityId) {
        query.push("remote_utility_id", remoteUtilityId);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.result;
};

export const getDeviceAllocationHistory = async ({
  meter_number,
  device_no,
  remoteUtilityId,
}: {
  meter_number: any;
  device_no: any;
  remoteUtilityId: string;
}) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "device-allocation-history",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      if (meter_number) query.push("meter_number", meter_number.toString());
      if (device_no) query.push("device_no", device_no.toString());
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.result;
};

export const getMeterWorkorderInfo = async ({
  meterId,
  remoteUtilityId,
}: {
  meterId: any;
  remoteUtilityId: string;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-workorder-info",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_id", meterId.toString());
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.result;
};

export const updatePhysicalStatus = async (
  resourceId: string,
  payload: {
    physical_status: number;
    premise?: string;
    unit_code?: string;
    warehouse?: string;
    remark?: string;
  }
): Promise<any> => {
  const url = ApiEndpoints.createUrl("mx", `device-management/${resourceId}/physical-status`);
  const response = await mxApiClient.patch(url, payload);
  return response.data;
};

export const updateUnit = async (
  resourceId: string,
  payload: {
    unit_code: string;
    premise: string;
  }
): Promise<any> => {
  const url = ApiEndpoints.createUrl("mx", `resource-management/update-unit/${resourceId}`);
  const response = await mxApiClient.patch(url, payload);
  return response.data;
};

export const getActivities = async (resourceId: string): Promise<any> => {
  const url = ApiEndpoints.createUrl("mx", `resource-management/activities/${resourceId}`);
  const response = await mxApiClient.get(url);
  return response.data;
};

export const getSubResources = async ({
  resourceId,
  search,
}: {
  resourceId: string;
  search?: string;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `resource-management/sub-resources/${resourceId}`,
    (query) => {
      if (search) {
        query.push("search", search);
      }
    }
  );
  const response = await mxApiClient.get(url);
  return response.data;
};

export const generateMeterNumber = async (
  remoteUtilityId: string,
): Promise<{ meterNumber: string }> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "generate-meter-number",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    },
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<{ meterNumber: string }>(
    response.data.result,
  );
};

export const editResource = async (
  resourceId: string,
  payload: any
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithPathVariables("mx", "device-management", [resourceId]);
  const response = await mxApiClient.patch(url, payload);
  return response.data;
};

export const searchMasterMeters = async ({
  remoteUtilityId,
  search,
}: {
  remoteUtilityId: string;
  search?: string;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_master", "true");
      if (search) {
        query.push("search", search);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.results;
};



export const getServiceOrdersJobCard = async ({
  remoteUtilityId,
  searchData,
}: {
  remoteUtilityId: string;
  searchData?: string;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "service-order-search",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      if (searchData) {
        query.push("search", searchData);
      }
    },
  );

  const response = await mxApiClient.get(url);
  return response.data.result ?? null;
};
